import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './W_PaymentScenarioPurchase.module.css';
import O_PlanRadio from '@/components/O_Organisms/O_PlanRadio/O_PlanRadio';
import A_CostResult from '@/components/A_Atoms/A_CostResult/A_CostResult';
import W_PaymentActions from '../W_PaymentActions/W_PaymentActions';
import { generateRequestId } from '@/utils/generators';
import { useTimer } from '@/hooks/useTimer';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { getScenarioById } from '@/constants/capsuleScenarios';
import { capsulesApi } from '@/services/capsulesApi';
import { useAuth } from '@/contexts/AuthContext';

export default function W_PaymentScenarioPurchase() {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getCurrentUser } = useAuth();
  const scenarioId = searchParams.get('scenarioId');
  const capsuleId = searchParams.get('capsuleId');
  const [requestId] = useMemo(() => [generateRequestId()], []);
  const remainingTime = useTimer(15);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const scenario = useMemo(() => {
    return scenarioId ? getScenarioById(scenarioId) : null;
  }, [scenarioId]);

  const price = scenario?.price ?? 0;
  const scenarioTitle = scenario
    ? t(`capsuleScenarios.${scenario.translationKey}.title`)
    : '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!scenarioId || !capsuleId) {
      setError('Отсутствуют необходимые параметры для покупки');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await capsulesApi.purchaseScenario(
        capsuleId,
        scenarioId
      );

      if (response.success) {
        await getCurrentUser();

        setSuccess(true);
        setError(null);

        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        setError(response.message || 'Ошибка при покупке сценария');
      }
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка при покупке сценария');
    } finally {
      setIsLoading(false);
    }
  };

  if (!scenario) {
    return null;
  }

  if (!capsuleId) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <p className={styles.error}>
            {t('payment.scenarioPurchase.missingCapsuleId') ||
              'Отсутствует ID капсулы'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.wrapper} onSubmit={handleSubmit}>
      <div className={styles.content}>
        <h4 className={styles.title}>
          {t('payment.scenarioPurchase.purchaseScenario')}
        </h4>
        {success && (
          <div className={styles.success}>
            {t('payment.scenarioPurchase.success') ||
              'Сценарий успешно куплен! Средства списаны с баланса.'}
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.plans_wrapper}>
          <O_PlanRadio
            name={scenario.id}
            value={scenario.id}
            label={scenarioTitle}
            price={price}
            isDiscounted={false}
            discount={0}
            isActive={true}
            showPriceSuffix={false}
          />
        </div>
        <div className={styles.cost_result_wrapper}>
          <A_CostResult
            number={price}
            text={t('payment.subscribePlan.preliminaryTotal')}
            isDiscounted={false}
          />
          <A_CostResult
            number={price}
            text={t('payment.subscribePlan.total')}
            isDiscounted={false}
          />
        </div>
      </div>
      <W_PaymentActions
        price={price}
        remainingTime={remainingTime}
        requestId={requestId}
        isAmountValid={true}
        isLoading={isLoading}
      />
    </form>
  );
}
